import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, useParams, useLocation, Link } from "react-router-dom";
import SideBar from "../../components/SideBar/SideBar";
import TopNav from "../../components/TopNav/TopNav";
import api from "../../services/api";
import CalculateAge from "../../utils/CalculateAge";
import areaCodeRegion from "../../utils/areaCodeRegion";
import { getGeo, getActivity, geoLabel } from "../../services/posthogGeo";
import profileDefault from "../../assets/images/profileDefault.png";
import "../Users/Users.scss";

// Detail view for a single soft-deleted account, opened from the eye icon
// (or row) on /deleted-users. Surfaces everything support needs to decide
// whether to reach out before the reaper hard-deletes: the deletion notes,
// how long they'd been a member, their profile info, and their in-app
// activity over the 30 days leading up to the deletion request.
//
// Front-end only — composes three existing endpoints:
//   GET /users/:id                      → profile (birthday, gender, phone, created_at…)
//   GET /users/admin/deleted-recent     → deletion meta fallback for direct loads
//   GET /analytics/user-activity/:uid   → activity feed (filtered to the pre-deletion window)

// Human labels for the app's engagement events. Covers the current PostHog
// taxonomy (onboarding, browsing, intent) plus older event names. Unmapped
// types fall back to their raw name, so nothing is ever hidden.
const EVENT_TYPE_LABELS = {
  // onboarding / account
  signup_completed: "Signed up",
  profile_photo_added: "Added profile photo",
  gallery_photo_added: "Added gallery photo",
  bio_saved: "Saved bio",
  notif_soft_ask_shown: "Saw notifications ask",
  notif_soft_ask_dismissed: "Dismissed notifications ask",
  // app / navigation
  app_opened: "Opened app",
  home_feed_viewed: "Viewed home feed",
  discover_opened: "Opened Discover",
  discover_screen_viewed: "Viewed Discover",
  discover_row_viewed: "Browsed a Discover row",
  map_opened: "Opened map",
  user_profile_viewed: "Viewed a profile",
  // venue / event intent
  venue_view: "Viewed venue",
  venue_tap: "Tapped venue",
  event_view: "Viewed event",
  event_tap: "Tapped event",
  venue_favorited: "Favorited venue",
  view_tables_clicked: "Viewed tables",
  join_guestlist_clicked: "Tried to join guestlist",
  bottle_service_view: "Viewed bottle service",
  bottle_purchase_started: "Started bottle purchase",
  search: "Searched",
  ai_query: "Asked AI",
  // reviews (review_prompt_requested was the pre-2026-07-11 native prompt)
  review_prompt_requested: "Prompted for review",
  review_prompt_shown: "Saw review prompt",
  review_prompt_rated: "Tapped rate on review prompt",
  review_prompt_dismissed: "Dismissed review prompt",
  review_prompt_skipped: "Skipped review prompt",
  // ads
  ad_impression: "Saw ad",
  ad_click: "Tapped ad",
  ad_dismiss: "Dismissed ad",
  // app-update nags
  app_update_popup_shown: "Saw app popup",
  app_update_popup_tapped: "Tapped app popup",
  force_update_shown: "Hit update screen",
  force_update_tapped: "Tapped Update Now",
};

// PostHog bookkeeping events ($set, $identify, $pageview…) aren't user
// actions — drop them so counts and the timeline reflect real behaviour.
const isInternalEvent = (t) => typeof t === "string" && t.startsWith("$");

const REASON_LABELS = {
  not_using: { label: "Not using", color: "#6B7280" },
  bug: { label: "Bug / app didn't work", color: "#EF4444" },
  privacy: { label: "Privacy", color: "#F59E0B" },
  too_expensive: { label: "Too expensive", color: "#0A3FD6" },
  other: { label: "Other", color: "#4F30EA" },
};

const DAY_MS = 24 * 60 * 60 * 1000;
const WINDOW_DAYS = 30;

const DeletedUserDetail = () => {
  const { userId } = useParams(); // Mongo _id
  const navigate = useNavigate();
  const location = useLocation();

  // Deletion meta (reason, notes, deleted_at, firebase id…). Prefilled from
  // the row we navigated from so the page paints instantly; refetched below
  // when the page is opened via a direct URL (no router state).
  const [meta, setMeta] = useState(location.state?.row || null);
  const [profile, setProfile] = useState(null);
  const [activity, setActivity] = useState([]);
  const [loadingProfile, setLoadingProfile] = useState(true);
  const [loadingActivity, setLoadingActivity] = useState(true);
  const [restoring, setRestoring] = useState(false);
  // Reverse-geocoded stated profile location (the city the user set on their
  // account), resolved from the raw "lat, lng" the app stores.
  const [cityLabel, setCityLabel] = useState("");
  // Direct-message activity (NOT tracked in PostHog — pulled from the admin
  // user-inboxes API, which searches chats by participant name/email).
  const [chats, setChats] = useState([]);
  const [loadingChats, setLoadingChats] = useState(true);
  // Where they actually connected from (IP + geo-IP), from PostHog.
  const [geo, setGeo] = useState(null);

  // Profile: birthday, gender, phone, email, created_at (member since).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoadingProfile(true);
        const { data } = await api.get(`/users/${userId}`);
        if (!cancelled) setProfile(data?.user || null);
      } catch (err) {
        console.error("Failed to fetch deleted user profile:", err);
        if (!cancelled) setProfile(null);
      } finally {
        if (!cancelled) setLoadingProfile(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [userId]);

  // Direct-load fallback: recover deletion meta (notes, reason, firebase id,
  // deleted_at) from the recent-deletions list when we arrived without state.
  useEffect(() => {
    if (meta) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/users/admin/deleted-recent`, {
          params: { days: WINDOW_DAYS },
        });
        const row = (data?.data || []).find((u) => u._id === userId);
        if (!cancelled && row) setMeta(row);
      } catch (err) {
        console.error("Failed to fetch deletion meta:", err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [meta, userId]);

  // Activity keyed on the Firebase UID (analytics distinct_id === users.id).
  // Request a wide 90-day window, then trim to the 30 days ending at the
  // deletion timestamp so we only show pre-deletion behaviour.
  const firebaseUid = meta?.id || profile?.id;
  const deletedAt = meta?.deleted_at || profile?.deleted_at || null;

  useEffect(() => {
    if (!firebaseUid) {
      // No UID to key on. Keep the spinner while the profile is still
      // resolving (it may yet supply an id); once that's done with nothing
      // to key on, stop and fall through to the empty state.
      if (!loadingProfile) setLoadingActivity(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        setLoadingActivity(true);
        // Pull real client events from PostHog (the admin /analytics/user-activity
        // endpoint reads a separate, near-empty Mongo collection keyed by mongo _id).
        const res = await getActivity(firebaseUid, { until: deletedAt || undefined, days: WINDOW_DAYS });
        if (cancelled) return;
        const end = deletedAt ? new Date(deletedAt) : new Date();
        const start = new Date(end.getTime() - WINDOW_DAYS * DAY_MS);
        const trimmed = (res?.activity || [])
          .filter((a) => !isInternalEvent(a.event_type))
          .filter((a) => {
            const t = new Date(a.created_at);
            return t >= start && t <= end;
          })
          .sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
        setActivity(trimmed);
      } catch (err) {
        console.error("Failed to fetch user activity:", err);
        if (!cancelled) setActivity([]);
      } finally {
        if (!cancelled) setLoadingActivity(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [firebaseUid, deletedAt, meta, loadingProfile]);

  // Resolve the stated profile location ("lat, lng") to a readable city via a
  // free, no-key reverse-geocode. If it's already a place name, show as-is.
  // Same approach as the UserProfile page.
  useEffect(() => {
    const loc = profile?.location;
    if (profile?.location_sharing === false || !loc) {
      setCityLabel("");
      return;
    }
    const coords = /^\s*(-?\d{1,3}(?:\.\d+)?)\s*,\s*(-?\d{1,3}(?:\.\d+)?)\s*$/.exec(loc);
    if (!coords) {
      setCityLabel(loc);
      return;
    }
    const [, lat, lng] = coords;
    let cancelled = false;
    setCityLabel("Locating…");
    fetch(`https://api.bigdatacloud.net/data/reverse-geocode-client?latitude=${lat}&longitude=${lng}&localityLanguage=en`)
      .then((r) => r.json())
      .then((d) => {
        if (cancelled) return;
        const city = d.city || d.locality || d.principalSubdivision;
        const region = d.principalSubdivision || d.countryName;
        setCityLabel(city ? (region && region !== city ? `${city}, ${region}` : city) : loc);
      })
      .catch(() => {
        if (!cancelled) setCityLabel(loc);
      });
    return () => {
      cancelled = true;
    };
  }, [profile?.location, profile?.location_sharing]);

  // Pull this user's direct-message activity. The admin user-inboxes endpoint
  // searches chats by participant firstName/lastName/email, so we query by the
  // user's email (unique) and fall back to their name, then keep only chats
  // this user is actually in.
  useEffect(() => {
    if (loadingProfile) return;
    const q = (profile?.email || `${profile?.firstName || ""} ${profile?.lastName || ""}`.trim());
    if (!q) {
      setChats([]);
      setLoadingChats(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        setLoadingChats(true);
        const { data } = await api.get(`/admin/user-inboxes`, { params: { q, records: 100 } });
        if (cancelled) return;
        const isSelf = (p) =>
          (p?._id && String(p._id) === String(userId)) ||
          (profile?.email && p?.email && p.email.toLowerCase() === profile.email.toLowerCase());
        const mine = (data?.data || []).filter((row) =>
          (row.participants || []).some(isSelf)
        );
        setChats(mine);
      } catch (err) {
        console.error("Failed to fetch user inboxes:", err);
        if (!cancelled) setChats([]);
      } finally {
        if (!cancelled) setLoadingChats(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [loadingProfile, profile?.email, profile?.firstName, profile?.lastName, userId]);

  // Where they connected from (IP + geo-IP), keyed on the firebase uid.
  useEffect(() => {
    const uid = meta?.id || profile?.id;
    if (!uid) return;
    let cancelled = false;
    getGeo(uid, meta?.deleted_at || profile?.deleted_at || undefined).then((res) => {
      if (!cancelled) setGeo(res?.geo || null);
    });
    return () => {
      cancelled = true;
    };
  }, [meta?.id, profile?.id, meta?.deleted_at, profile?.deleted_at]);

  const handleRestore = useCallback(async () => {
    if (restoring) return;
    if (!window.confirm("Restore this account? The user will be able to sign in again.")) return;
    setRestoring(true);
    try {
      await api.post(`/users/admin/restore/${userId}`, undefined, { timeout: 15000 });
      navigate("/deleted-users");
    } catch (err) {
      console.error("Restore failed:", err);
      const msg =
        err?.code === "ECONNABORTED"
          ? "Restore timed out. Try again."
          : err?.response?.data?.message || "Restore failed. Try again.";
      window.alert(msg);
    } finally {
      setRestoring(false);
    }
  }, [restoring, userId, navigate]);

  // ZONA runs on Miami time — stamp everything in America/New_York so the
  // exact signup/deletion hour reads correctly regardless of viewer locale.
  const fmtMiami = (d) => {
    if (!d) return "—";
    try {
      return (
        new Date(d).toLocaleString("en-US", {
          timeZone: "America/New_York",
          month: "short",
          day: "numeric",
          year: "numeric",
          hour: "numeric",
          minute: "2-digit",
          hour12: true,
        }) + " ET"
      );
    } catch {
      return "—";
    }
  };
  // Compact variant for the activity rows (no year/ET, includes seconds so
  // same-minute bursts are distinguishable).
  const fmtMiamiShort = (d) => {
    if (!d) return "—";
    try {
      return new Date(d).toLocaleString("en-US", {
        timeZone: "America/New_York",
        month: "short",
        day: "numeric",
        hour: "numeric",
        minute: "2-digit",
        second: "2-digit",
        hour12: true,
      });
    } catch {
      return "—";
    }
  };

  const humanizeSpan = (ms) => {
    if (ms == null || ms < 0) return null;
    const mins = Math.floor(ms / 60000);
    if (mins < 1) return "under a minute";
    if (mins < 60) return `${mins} min${mins === 1 ? "" : "s"}`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) {
      const m = mins % 60;
      return `${hrs}h${m ? ` ${m}m` : ""}`;
    }
    const days = Math.floor(hrs / 24);
    if (days < 60) {
      const h = hrs % 24;
      return `${days} day${days === 1 ? "" : "s"}${h ? ` ${h}h` : ""}`;
    }
    const months = Math.floor(days / 30);
    if (months < 12) return `${months} mo`;
    const yrs = Math.floor(months / 12);
    const mo = months % 12;
    return `${yrs} yr${yrs === 1 ? "" : "s"}${mo ? ` ${mo} mo` : ""}`;
  };

  // How long the account existed (signup → deletion). Drives the "all same
  // day?" answer without another prompt.
  const memberSince = profile?.created_at;
  const accountAgeMs = memberSince
    ? (deletedAt ? new Date(deletedAt) : new Date()) - new Date(memberSince)
    : null;
  const accountAge = humanizeSpan(accountAgeMs);
  const sameDay = accountAgeMs != null && accountAgeMs >= 0 && accountAgeMs < DAY_MS;

  const name = `${meta?.firstName || profile?.firstName || ""} ${
    meta?.lastName || profile?.lastName || ""
  }`.trim();
  const age = profile?.birthday ? CalculateAge(profile.birthday) : null;
  const phoneInfo = areaCodeRegion(profile?.phone);
  const email = profile?.email || meta?.email || "";
  const isPrivateRelay = /@privaterelay\.appleid\.com$/i.test(email);
  // Auto-flagged catfish/fraud signals from the fields we already have. The
  // IP-geo mismatch signal is added by the PostHog forensics section below.
  const signals = [];
  if (isPrivateRelay) signals.push("Apple Private Relay email (real address hidden)");
  if (sameDay && accountAge) signals.push(`Signed up and deleted the same day (${accountAge})`);
  if (phoneInfo?.region) signals.push(`Phone area code is ${phoneInfo.region}`);
  if (!loadingProfile && profile && !profile.location && profile.location_sharing !== false)
    signals.push("Never shared a location");
  // Direct-message signals (mass-DM / copy-paste openers are classic spam/catfish).
  const dmContents = chats.map((c) => (c.last_message?.content || "").trim()).filter(Boolean);
  const dmDup = (() => {
    const m = {};
    dmContents.forEach((t) => { m[t] = (m[t] || 0) + 1; });
    const d = Object.entries(m).filter(([, n]) => n > 1).sort((a, b) => b[1] - a[1]);
    return d[0] || null; // [text, count]
  })();
  if (chats.length >= 3) signals.push(`Opened direct chats with ${chats.length} people`);
  if (dmDup) signals.push(`Sent the same message ("${dmDup[0].slice(0, 40)}${dmDup[0].length > 40 ? "…" : ""}") to ${dmDup[1]} people`);
  const geoText = geoLabel(geo);
  if (geoText) signals.push(`Connected from ${geoText}${geo?.ip ? ` (IP ${geo.ip})` : ""}`);
  const reasonMeta = meta?.delete_reason
    ? REASON_LABELS[meta.delete_reason] || { label: meta.delete_reason, color: "#6B7280" }
    : null;

  // Activity summary metrics for the window. `activity` is sorted newest-first.
  const activeDays = new Set(
    activity.map((a) => {
      try {
        return new Date(a.created_at).toLocaleDateString("en-US", { timeZone: "America/New_York" });
      } catch {
        return a.created_at;
      }
    })
  ).size;
  const lastAction = activity[0]?.created_at;
  const firstAction = activity[activity.length - 1]?.created_at;
  // Full per-type breakdown (most frequent first); internal events already removed.
  const breakdown = Object.entries(
    activity.reduce((acc, a) => {
      const k = EVENT_TYPE_LABELS[a.event_type] || a.event_type || "Other";
      acc[k] = (acc[k] || 0) + 1;
      return acc;
    }, {})
  ).sort((a, b) => b[1] - a[1]);

  const getDetail = (a) => {
    if (a.venue_name) return a.venue_name;
    if (a.query) return `"${a.query}"`;
    return "";
  };

  return (
    <main>
      <TopNav />
      <div className="main-container">
        <SideBar />
        <article className="page-content" style={{ padding: 24 }}>
          {/* Header */}
          <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 20, flexWrap: "wrap" }}>
            <button onClick={() => navigate("/deleted-users")} style={backBtn}>
              ← Back
            </button>
            <h1 style={{ margin: 0, color: "#fff", fontSize: 22 }}>Deleted User</h1>
            <div style={{ marginLeft: "auto", display: "flex", gap: 8, flexWrap: "wrap" }}>
              <Link to="/shared-devices" style={{ ...backBtn, textDecoration: "none", display: "inline-flex", alignItems: "center" }}>
                Shared Devices
              </Link>
              <Link to={`/users/${userId}`} style={{ ...backBtn, textDecoration: "none", display: "inline-flex", alignItems: "center" }}>
                View full profile
              </Link>
              <button onClick={handleRestore} disabled={restoring} style={restoreBtn(restoring)}>
                {restoring ? "Restoring…" : "Restore account"}
              </button>
            </div>
          </div>

          {/* Profile card */}
          <div style={card}>
            <div style={{ display: "flex", gap: 16, alignItems: "center", marginBottom: 18, flexWrap: "wrap" }}>
              <img
                src={profile?.photo_url || meta?.photo_url || profileDefault}
                alt=""
                onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = profileDefault; }}
                style={{ width: 72, height: 72, borderRadius: 36, objectFit: "cover", background: "#222" }}
              />
              <div>
                <div style={{ color: "#fff", fontSize: 20, fontWeight: 700 }}>{name || "—"}</div>
                <div style={{ color: "rgba(255,255,255,0.55)", fontSize: 13, marginTop: 4 }}>
                  {age != null && typeof age === "number" ? `${age} y.o.` : "Age unknown"}
                  {profile?.gender ? ` · ${profile.gender}` : ""}
                  {profile?.ig ? ` · IG @${profile.ig}` : ""}
                </div>
              </div>
              {reasonMeta && (
                <span
                  style={{
                    marginLeft: "auto",
                    background: reasonMeta.color,
                    color: "#fff",
                    fontSize: 12,
                    fontWeight: 600,
                    padding: "5px 12px",
                    borderRadius: 999,
                  }}
                >
                  {reasonMeta.label}
                </span>
              )}
            </div>

            {!loadingProfile && signals.length > 0 && (
              <div
                style={{
                  background: "rgba(245,158,11,0.10)",
                  border: "1px solid rgba(245,158,11,0.35)",
                  borderRadius: 10,
                  padding: "10px 14px",
                  marginBottom: 16,
                }}
              >
                <div style={{ color: "#F59E0B", fontSize: 12, fontWeight: 700, letterSpacing: 0.5, marginBottom: 6 }}>
                  Signals to review
                </div>
                <ul style={{ margin: 0, paddingLeft: 18, color: "rgba(255,255,255,0.85)", fontSize: 13, lineHeight: 1.6 }}>
                  {signals.map((s, i) => (
                    <li key={i}>{s}</li>
                  ))}
                </ul>
              </div>
            )}

            <div style={grid}>
              <Field label="Email" value={loadingProfile ? "…" : profile?.email || meta?.email || "—"} />
              <Field
                label="Phone"
                value={
                  loadingProfile ? (
                    "…"
                  ) : profile?.phone ? (
                    <span>
                      {profile.phone}
                      {phoneInfo && (
                        <span style={{ color: "rgba(255,255,255,0.5)", fontSize: 12 }}>
                          {"  "}· {phoneInfo.region ? phoneInfo.region : `area ${phoneInfo.code}`}
                        </span>
                      )}
                    </span>
                  ) : (
                    "—"
                  )
                }
              />
              <Field
                label="Profile location"
                value={
                  loadingProfile
                    ? "…"
                    : profile?.location_sharing === false
                    ? "Location hidden"
                    : cityLabel || "—"
                }
              />
              <Field
                label="Connected from (IP geo)"
                value={
                  geoText ? (
                    <span>
                      {geoText}
                      {geo?.ip && (
                        <span style={{ display: "block", color: "rgba(255,255,255,0.4)", fontSize: 11, fontFamily: "monospace", marginTop: 2, wordBreak: "break-all" }}>
                          {geo.ip}
                        </span>
                      )}
                    </span>
                  ) : (
                    "—"
                  )
                }
              />
              <Field
                label="Signed up"
                value={loadingProfile ? "…" : memberSince ? fmtMiami(memberSince) : "—"}
              />
              <Field label="Deletion requested" value={fmtMiami(deletedAt)} />
              <Field
                label="Account age at deletion"
                value={
                  loadingProfile
                    ? "…"
                    : accountAge
                    ? sameDay
                      ? `${accountAge} · same day`
                      : accountAge
                    : "—"
                }
              />
              <Field
                label="Days until purge"
                value={
                  meta?.days_remaining != null
                    ? meta.days_remaining <= 1
                      ? "Final 24h"
                      : `${meta.days_remaining} days`
                    : "—"
                }
              />
              <Field label="App version" value={meta?.app_version || "—"} />
              <Field
                label="Device"
                value={
                  loadingProfile
                    ? "…"
                    : profile?.device_id
                    ? <span style={{ fontFamily: "monospace", fontSize: 12 }}>{profile.device_id}</span>
                    : "—"
                }
              />
            </div>
          </div>

          {/* Deletion notes */}
          <div style={card}>
            <div style={sectionTitle}>Deletion notes</div>
            <div
              style={{
                color: meta?.delete_reason_notes ? "#fff" : "rgba(255,255,255,0.4)",
                fontSize: 14,
                lineHeight: 1.6,
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
              }}
            >
              {meta?.delete_reason_notes || "No notes left with this deletion."}
            </div>
          </div>

          {/* Direct messages — NOT in PostHog; pulled from chat records */}
          <div style={card}>
            <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", flexWrap: "wrap", gap: 8 }}>
              <div style={sectionTitle}>Direct messages</div>
              {!loadingChats && (
                <span style={{ color: "rgba(255,255,255,0.6)", fontSize: 12 }}>
                  <strong style={{ color: "#fff" }}>{chats.length}</strong> chat{chats.length === 1 ? "" : "s"}
                </span>
              )}
            </div>
            <div style={{ overflowX: "auto", marginTop: 12 }}>
              <table style={{ width: "100%", borderCollapse: "collapse", color: "#fff", minWidth: 640 }}>
                <thead>
                  <tr style={{ background: "rgba(255,255,255,0.04)" }}>
                    <th style={th}>With</th>
                    <th style={th}>Last message</th>
                    <th style={th}>Type</th>
                    <th style={th}>Updated</th>
                  </tr>
                </thead>
                <tbody>
                  {loadingChats ? (
                    <tr><td colSpan={4} style={{ ...td, textAlign: "center", padding: 24, opacity: 0.6 }}>Loading…</td></tr>
                  ) : chats.length === 0 ? (
                    <tr><td colSpan={4} style={{ ...td, textAlign: "center", padding: 24, opacity: 0.6 }}>No direct messages found.</td></tr>
                  ) : (
                    chats.map((c) => {
                      const others = (c.participants || [])
                        .filter((p) => !(
                          (p?._id && String(p._id) === String(userId)) ||
                          (profile?.email && p?.email && p.email.toLowerCase() === profile.email.toLowerCase())
                        ))
                        .map((p) => `${p.firstName || ""} ${p.lastName || ""}`.trim() || p.email || (p._id ? String(p._id).slice(-6) : "?"));
                      const firstOther = (c.participants || []).find((p) => !(
                        (p?._id && String(p._id) === String(userId)) ||
                        (profile?.email && p?.email && p.email.toLowerCase() === profile.email.toLowerCase())
                      ));
                      const body = (c.last_message?.content || "").trim();
                      return (
                        <tr key={c._id} style={{ borderTop: "1px solid rgba(255,255,255,0.05)" }}>
                          <td style={td}>
                            {firstOther?._id ? (
                              <Link to={`/users/${firstOther._id}`} style={{ color: "#8AA2FF" }}>
                                {others.join(", ") || "—"}
                              </Link>
                            ) : (
                              others.join(", ") || "—"
                            )}
                          </td>
                          <td style={{ ...td, maxWidth: 340, wordBreak: "break-word" }}>
                            {body || <span style={{ opacity: 0.4 }}>—</span>}
                          </td>
                          <td style={{ ...td, textTransform: "capitalize" }}>{c.type || "—"}</td>
                          <td style={{ ...td, whiteSpace: "nowrap", color: "rgba(255,255,255,0.6)" }}>
                            {c.updatedAt ? fmtMiamiShort(c.updatedAt) : "—"}
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
            <div style={{ color: "rgba(255,255,255,0.4)", fontSize: 11, marginTop: 10 }}>
              Messaging is not tracked in PostHog. Pulled from chat records; shows each chat's latest message.
            </div>
          </div>

          {/* Activity in the 30 days before deletion */}
          <div style={card}>
            <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", flexWrap: "wrap", gap: 8 }}>
              <div style={sectionTitle}>Activity: 30 days before deletion</div>
              {!loadingActivity && (
                <div style={{ display: "flex", gap: 18, color: "rgba(255,255,255,0.6)", fontSize: 12, flexWrap: "wrap" }}>
                  <span><strong style={{ color: "#fff" }}>{activity.length}</strong> actions</span>
                  <span><strong style={{ color: "#fff" }}>{activeDays}</strong> active day{activeDays === 1 ? "" : "s"}</span>
                  {firstAction && <span>First {fmtMiamiShort(firstAction)}</span>}
                  {lastAction && <span>Last {fmtMiamiShort(lastAction)}</span>}
                </div>
              )}
            </div>

            {!loadingActivity && breakdown.length > 0 && (
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8, margin: "14px 0 4px" }}>
                {breakdown.map(([label, count]) => (
                  <span key={label} style={chip}>
                    {label} · {count}
                  </span>
                ))}
              </div>
            )}

            <div style={{ overflowX: "auto", marginTop: 12 }}>
              <table style={{ width: "100%", borderCollapse: "collapse", color: "#fff", minWidth: 640 }}>
                <thead>
                  <tr style={{ background: "rgba(255,255,255,0.04)" }}>
                    <th style={th}>Time</th>
                    <th style={th}>Action</th>
                    <th style={th}>Detail</th>
                    <th style={th}>Source</th>
                  </tr>
                </thead>
                <tbody>
                  {loadingActivity ? (
                    <tr>
                      <td colSpan={4} style={{ ...td, textAlign: "center", padding: 28, opacity: 0.6 }}>
                        Loading…
                      </td>
                    </tr>
                  ) : activity.length === 0 ? (
                    <tr>
                      <td colSpan={4} style={{ ...td, textAlign: "center", padding: 28, opacity: 0.6 }}>
                        No tracked activity in the 30 days before deletion.
                      </td>
                    </tr>
                  ) : (
                    activity.map((a, i) => (
                      <tr key={i} style={{ borderTop: "1px solid rgba(255,255,255,0.05)" }}>
                        <td style={{ ...td, whiteSpace: "nowrap", color: "rgba(255,255,255,0.6)" }}>
                          {fmtMiamiShort(a.created_at)}
                        </td>
                        <td style={td}>{EVENT_TYPE_LABELS[a.event_type] || a.event_type}</td>
                        <td style={td}>{getDetail(a)}</td>
                        <td style={{ ...td, color: "rgba(255,255,255,0.4)" }}>{a.source || "—"}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </article>
      </div>
    </main>
  );
};

const Field = ({ label, value }) => (
  <div>
    <div style={{ color: "rgba(255,255,255,0.45)", fontSize: 11, letterSpacing: 1, textTransform: "uppercase", marginBottom: 4 }}>
      {label}
    </div>
    <div style={{ color: "#fff", fontSize: 14, wordBreak: "break-word" }}>{value}</div>
  </div>
);

const card = {
  background: "rgba(255,255,255,0.03)",
  border: "1px solid rgba(255,255,255,0.06)",
  borderRadius: 12,
  padding: 20,
  marginBottom: 16,
};
const grid = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
  gap: 18,
};
const sectionTitle = {
  color: "rgba(255,255,255,0.85)",
  fontSize: 13,
  fontWeight: 700,
  letterSpacing: 1,
  textTransform: "uppercase",
};
const chip = {
  background: "rgba(79,48,234,0.18)",
  border: "1px solid rgba(79,48,234,0.35)",
  color: "#fff",
  fontSize: 12,
  padding: "4px 10px",
  borderRadius: 999,
};
const backBtn = {
  background: "rgba(255,255,255,0.08)",
  border: "1px solid rgba(255,255,255,0.12)",
  color: "#fff",
  padding: "8px 14px",
  borderRadius: 8,
  fontSize: 13,
  cursor: "pointer",
};
const restoreBtn = (busy) => ({
  background: busy ? "#555" : "#0A3FD6",
  color: "#fff",
  border: "none",
  padding: "8px 16px",
  borderRadius: 8,
  fontSize: 13,
  fontWeight: 600,
  cursor: busy ? "not-allowed" : "pointer",
  opacity: busy ? 0.7 : 1,
});
const th = {
  textAlign: "left",
  padding: "12px 14px",
  fontSize: 11,
  fontWeight: 600,
  letterSpacing: 1.5,
  textTransform: "uppercase",
  color: "rgba(255,255,255,0.6)",
};
const td = {
  padding: "12px 14px",
  fontSize: 13,
  verticalAlign: "middle",
};

export default DeletedUserDetail;
